import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ListTodo, Paperclip, Trash2, X, Check, Loader2, FileText } from 'lucide-react';
import { isAxiosError } from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Modal } from '@/components/ui/Modal';
import { cn, getFileUrl } from '@/lib/utils';
import { toast } from 'react-hot-toast';
import {
  quickLembretesService,
  MAX_ANEXO_BYTES,
  MAX_ANEXOS,
  type QuickLembrete,
} from '@/services/quickLembretesService';

function defaultDatetimeLocalValue(): string {
  const d = new Date();
  d.setHours(d.getHours() + 1, 0, 0, 0);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatLembreteDisplay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function apiErrorMessage(e: unknown): string {
  if (isAxiosError(e) && e.response?.data) {
    const m = (e.response.data as { message?: string }).message;
    if (typeof m === 'string' && m) return m;
  }
  return e instanceof Error ? e.message : 'Falha na operação. Tente novamente.';
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function validateFile(f: File): string | null {
  if (f.size > MAX_ANEXO_BYTES) return 'Ficheiro muito grande (máx. 10 MB).';
  return null;
}

export const QuickLembretesButton: React.FC = () => {
  const { user } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<QuickLembrete[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [dataHora, setDataHora] = useState(defaultDatetimeLocalValue);
  const [pendingAnexos, setPendingAnexos] = useState<File[]>([]);
  const anexoHiddenRef = useRef<HTMLInputElement>(null);
  const anexoLembreteTargetRef = useRef<string | null>(null);

  const userId = user?.id;

  const loadList = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!userId) return;
      const silent = options?.silent ?? false;
      if (!silent) setLoading(true);
      try {
        const list = await quickLembretesService.list();
        setItems(list);
      } catch (e) {
        toast.error(apiErrorMessage(e));
        if (!silent) setItems([]);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [userId]
  );

  useEffect(() => {
    if (!userId) {
      setItems([]);
      return;
    }
    void loadList();
  }, [userId, loadList]);

  useEffect(() => {
    if (open) {
      setDataHora(defaultDatetimeLocalValue());
      void loadList({ silent: true });
    }
  }, [open, loadList]);

  const pendentes = useMemo(
    () => items.filter((i) => !i.concluido).length,
    [items]
  );

  const sorted = useMemo(() => {
    return [...items].sort((a, b) => {
      if (a.concluido !== b.concluido) return a.concluido ? 1 : -1;
      return new Date(a.dataHora).getTime() - new Date(b.dataHora).getTime();
    });
  }, [items]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) {
      toast.error('Sessão inválida. Faça login novamente.');
      return;
    }
    const t = titulo.trim();
    if (!t) {
      toast.error('Informe o título do lembrete.');
      return;
    }
    if (!dataHora) {
      toast.error('Informe data e hora.');
      return;
    }
    const d = new Date(dataHora);
    if (Number.isNaN(d.getTime())) {
      toast.error('Data ou hora inválida.');
      return;
    }
    setSaving(true);
    const ficheirosPendentes = [...pendingAnexos];
    try {
      const lembrete = await quickLembretesService.create({
        titulo: t,
        descricao: descricao.trim(),
        dataHora: d.toISOString(),
      });
      for (const file of ficheirosPendentes) {
        const v = validateFile(file);
        if (v) {
          toast.error(`${file.name}: ${v}`);
          continue;
        }
        try {
          await quickLembretesService.uploadAnexo(lembrete.id, file);
        } catch (e) {
          toast.error(`${file.name}: ${apiErrorMessage(e)}`);
        }
      }
      setPendingAnexos([]);
      setTitulo('');
      setDescricao('');
      setDataHora(defaultDatetimeLocalValue());
      void loadList({ silent: true });
      toast.success('Lembrete adicionado' + (ficheirosPendentes.length > 0 ? ' (ficheiros enviados)' : '') + '.');
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id: string) => {
    const item = items.find((i) => i.id === id);
    if (!item) return;
    try {
      const updated = await quickLembretesService.update(id, {
        concluido: !item.concluido,
      });
      setItems((prev) => prev.map((i) => (i.id === id ? updated : i)));
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const handleRemove = async (id: string) => {
    try {
      await quickLembretesService.remove(id);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success('Lembrete removido.');
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const onPendingAnexosChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextFiles = Array.from(e.target.files || []);
    e.target.value = '';
    if (nextFiles.length === 0) return;
    setPendingAnexos((prev) => {
      const out = [...prev];
      for (const f of nextFiles) {
        if (out.length >= MAX_ANEXOS) {
          toast.error('Máximo de 10 ficheiros por lembrete.');
          break;
        }
        const err = validateFile(f);
        if (err) {
          toast.error(`${f.name}: ${err}`);
          continue;
        }
        out.push(f);
      }
      return out;
    });
  };

  const removePendingFile = (index: number) => {
    setPendingAnexos((p) => p.filter((_, i) => i !== index));
  };

  const startAddAnexo = (lembreteId: string) => {
    const row = items.find((i) => i.id === lembreteId);
    if ((row?.anexos.length ?? 0) >= MAX_ANEXOS) {
      toast.error('Máximo de 10 ficheiros por lembrete.');
      return;
    }
    anexoLembreteTargetRef.current = lembreteId;
    anexoHiddenRef.current?.click();
  };

  const handleAnexoPicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    const lembreteId = anexoLembreteTargetRef.current;
    anexoLembreteTargetRef.current = null;
    if (!f || !lembreteId) return;
    const err = validateFile(f);
    if (err) {
      toast.error(err);
      return;
    }
    const row = items.find((i) => i.id === lembreteId);
    if ((row?.anexos.length ?? 0) >= MAX_ANEXOS) {
      toast.error('Máximo de 10 ficheiros por lembrete.');
      return;
    }
    try {
      await quickLembretesService.uploadAnexo(lembreteId, f);
      void loadList({ silent: true });
      toast.success('Ficheiro anexado.');
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const handleRemoveAnexo = async (lembreteId: string, anexoId: string) => {
    try {
      await quickLembretesService.removeAnexo(lembreteId, anexoId);
      void loadList({ silent: true });
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  if (!user) return null;

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="relative rounded-input"
        onClick={() => setOpen(true)}
        title="Lembretes rápidos"
        aria-label={pendentes > 0 ? `Lembretes rápidos, ${pendentes} pendentes` : 'Lembretes rápidos'}
      >
        {loading && !open ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        ) : (
          <ListTodo className="h-4 w-4" aria-hidden />
        )}
        {pendentes > 0 && (
          <span
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-secondary px-1 text-[10px] font-medium leading-none text-secondary-foreground"
            aria-hidden
          >
            {pendentes > 9 ? '9+' : pendentes}
          </span>
        )}
      </Button>

      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        title="Lembretes rápidos"
        size="lg"
      >
        <p className="mb-4 text-body-sm text-muted-foreground">
          Lembretes pessoais (independentes de títulos) associados à sua conta e armazenados no servidor.
        </p>

        <form onSubmit={handleAdd} className="space-y-3 rounded-input border border-border bg-muted/30 p-3">
          <div className="grid gap-2 sm:grid-cols-2 sm:items-end">
            <Input
              label="Título"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Ex.: Ligar para o cliente"
              maxLength={500}
              required
            />
            <div className="space-y-2">
              <label
                className="font-body text-body-sm font-medium text-foreground"
                htmlFor="quick-lembrete-dt"
              >
                Data e hora
              </label>
              <input
                id="quick-lembrete-dt"
                type="datetime-local"
                value={dataHora}
                onChange={(e) => setDataHora(e.target.value)}
                className={cn(
                  'h-9 w-full rounded-input border border-input bg-background px-3 py-1.5 font-body text-body-sm',
                  'ring-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                )}
                required
              />
            </div>
          </div>
          <div>
            <label
              className="mb-2 block font-body text-body-sm font-medium text-foreground"
              htmlFor="quick-lembrete-desc"
            >
              Descrição (opcional)
            </label>
            <Textarea
              id="quick-lembrete-desc"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Notas adicionais…"
              rows={2}
              className="min-h-0 text-body-sm"
            />
          </div>
          <div className="space-y-2">
            <input
              id="new-lembrete-anexos"
              type="file"
              className="sr-only"
              multiple
              onChange={onPendingAnexosChange}
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => document.getElementById('new-lembrete-anexos')?.click()}
                disabled={saving || pendingAnexos.length >= MAX_ANEXOS}
                title="Até 10 ficheiros, 10 MB cada (PDF, imagem, Office, CSV, txt)"
              >
                <Paperclip className="h-3.5 w-3.5" aria-hidden />
                Anexar ficheiros
                {pendingAnexos.length > 0 && (
                  <span className="ml-1 rounded bg-muted px-1.5 text-body-xs">({pendingAnexos.length})</span>
                )}
              </Button>
              <span className="text-body-xs text-muted-foreground">Opcional, até 10 ficheiros.</span>
            </div>
            {pendingAnexos.length > 0 && (
              <ul className="flex flex-col gap-1.5 text-body-xs text-muted-foreground">
                {pendingAnexos.map((f, idx) => (
                  <li key={`${f.name}-${idx}`} className="flex items-center justify-between gap-2 rounded border border-border/60 bg-background px-2 py-1">
                    <span className="min-w-0 flex items-center gap-1.5 truncate">
                      <FileText className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      <span className="truncate">{f.name}</span>
                      <span className="shrink-0">({formatBytes(f.size)})</span>
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0"
                      onClick={() => removePendingFile(idx)}
                      title="Remover"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex justify-end">
            <Button type="submit" variant="secondary" disabled={saving}>
              {saving && <Loader2 className="mr-2 h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />}
              Adicionar
            </Button>
          </div>
        </form>

        <div className="mt-6">
          <h4 className="mb-2 font-body text-sm font-medium text-foreground">Seus lembretes</h4>
          {loading && open ? (
            <div className="flex items-center justify-center py-8 text-body-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 shrink-0 animate-spin" aria-hidden />
            </div>
          ) : sorted.length === 0 ? (
            <p className="rounded-input border border-dashed border-border bg-muted/20 p-4 text-center text-body-sm text-muted-foreground">
              Nenhum lembrete ainda. Use o formulário acima para criar o primeiro.
            </p>
          ) : (
            <ul className="max-h-[min(18rem,50vh)] space-y-2 overflow-y-auto pr-0.5">
              {sorted.map((item) => (
                <li
                  key={item.id}
                  className={cn(
                    'flex flex-col gap-1 rounded-input border border-border p-3 sm:flex-row sm:items-start sm:justify-between',
                    item.concluido && 'opacity-70'
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <p
                      className={cn(
                        'font-body text-sm font-medium text-foreground',
                        item.concluido && 'line-through'
                      )}
                    >
                      {item.titulo}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatLembreteDisplay(item.dataHora)}
                    </p>
                    {item.descricao && (
                      <p className="mt-1 text-body-sm text-muted-foreground line-clamp-3">
                        {item.descricao}
                      </p>
                    )}
                    {item.anexos && item.anexos.length > 0 && (
                      <ul className="mt-2 space-y-1 border-t border-border/60 pt-2">
                        {item.anexos.map((a) => (
                          <li
                            key={a.id}
                            className="flex min-w-0 items-center justify-between gap-1 text-left text-body-xs"
                          >
                            <a
                              href={getFileUrl(a.url)}
                              target="_blank"
                              rel="noreferrer"
                              className="min-w-0 truncate text-primary hover:underline"
                              title={a.originalName}
                            >
                              {a.originalName}
                            </a>
                            <span className="shrink-0 text-muted-foreground">{formatBytes(a.fileSize)}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 shrink-0"
                              title="Remover anexo"
                              onClick={() => void handleRemoveAnexo(item.id, a.id)}
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-1 self-end sm:self-start">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      title="Anexar ficheiro"
                      disabled={item.concluido || ((item.anexos?.length ?? 0) >= MAX_ANEXOS)}
                      onClick={() => startAddAnexo(item.id)}
                    >
                      <Paperclip className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      title={item.concluido ? 'Reabrir' : 'Concluir'}
                      onClick={() => void handleToggle(item.id)}
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      title="Excluir"
                      onClick={() => void handleRemove(item.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Modal>
      <input
        ref={anexoHiddenRef}
        type="file"
        className="sr-only"
        onChange={handleAnexoPicked}
        accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt,.odt,application/pdf,image/*"
      />
    </>
  );
};
